import { AgGridTable } from '@components/AgGrid/AgGridTable';
import type { CatalogRatioGroupData, CatalogRatioShortData, CatalogUerBaseCostData } from '@gql/graphql';
import { DismantlingCoefficientTypeEnum, RatioTypeEnum } from '@gql/graphql';

import type { IDatasource, SortChangedEvent } from 'ag-grid-community';
import type React from 'react';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { getRowClass, getRowId } from '../../helpers';
import { useMassApplicationBaseCostsColumns, useMassApplicationBaseCostsOutputData } from '../../hooks';
import Styles from '../../massRatioApplication.module.css';
import type { TRatioTypeSelectItem, TTableFiltersProps } from '../../types';

type TSelectBaseCostsStepProps = {
    selectedRatioType: TRatioTypeSelectItem | null;
    selectedRatioGroup: CatalogRatioGroupData | null;
    checkedRatios: Record<string, CatalogRatioShortData | null>;
    tableUniqueId: string;
    changeFilterHandler: (filter: TTableFiltersProps) => void;
    responseRatioType?: RatioTypeEnum;
    baseCostsData: CatalogUerBaseCostData[];
    checkedBaseCosts: Record<string, CatalogUerBaseCostData | null>;
    setCheckedBaseCosts: React.Dispatch<React.SetStateAction<Record<string, CatalogUerBaseCostData | null>>>;
    changeOrderHandler: (params: SortChangedEvent) => void;
    ratioGroupIds: string[];
    selectedSubgroup: CatalogRatioGroupData | null;
    baseCostDataSource: IDatasource;
};

export const SelectBaseCostsStep = ({
    tableUniqueId,
    selectedRatioGroup,
    responseRatioType,
    changeFilterHandler,
    baseCostsData,
    checkedBaseCosts,
    setCheckedBaseCosts,
    changeOrderHandler,
    ratioGroupIds,
    selectedSubgroup,
    baseCostDataSource,
}: TSelectBaseCostsStepProps) => {
    const { id: catalogUniqueId } = useParams();

    const { currentBaseCostsRowData, updateGroupBaseCostsRowData } = useMassApplicationBaseCostsOutputData({
        selectedRatioGroup,
        responseRatioType,
        baseCostsData,
        selectedSubgroup,
    });

    const canCheckRow = useMemo(
        () =>
            !(
                responseRatioType === RatioTypeEnum.ConstraintCoef ||
                selectedRatioGroup?.groupType === DismantlingCoefficientTypeEnum.EquipmentInstallation
            ),
        [responseRatioType, selectedRatioGroup?.groupType],
    );

    const handleChangeCheckbox = useCallback(
        (row: CatalogUerBaseCostData) =>
            ({ checked }: { checked: boolean }) => {
                setCheckedBaseCosts((ids) => ({
                    ...ids,
                    [row.id]: checked,
                }));
            },
        [setCheckedBaseCosts],
    );

    const { columns: baseCostColumns } = useMassApplicationBaseCostsColumns({
        catalogUniqueId,
        changeFilterHandler,
        currentRowData: currentBaseCostsRowData,
        handleChangeCheckbox,
        checkedIds: checkedBaseCosts,
        canCheckRow,
        canCheckGroupRow: false,
        ratioType: responseRatioType,
    });

    return (
        <div id="addCoefficient" className={`AddCoefficient ${Styles.ratioApplicationTable}`}>
            <AgGridTable
                getRowId={getRowId}
                rowData={updateGroupBaseCostsRowData}
                columnDefs={baseCostColumns}
                rowClassRules={getRowClass}
                rowSelection="multiple"
                rowHeight={40}
                onSortChanged={changeOrderHandler}
                key={tableUniqueId}
                context={{ tableUniqueId }}
                overlayNoRowsTemplate="Нет данных"
                datasource={baseCostDataSource}
                maxConcurrentDatasourceRequests={1}
                maxBlocksInCache={50}
                cacheBlockSize={50}
                rowModelType="infinite"
            />
        </div>
    );
};

SelectBaseCostsStep.displayName = 'SelectBaseCostsStep';
